import React, { ReactNode } from 'react';
import { Row, Col, Button, Dropdown, Tooltip } from 'antd';
import { ItemType } from 'antd/lib/menu/hooks/useItems';
import classNames from 'classnames';
import { ReactComponent as ActionSvg } from '@assets/icons/action.svg';
import { ReactComponent as CrossSvg } from '@assets/icons/cross-xs.svg';
import { ReactComponent as PinSecondarySvg } from '@assets/icons/pin-secondary.svg';
import { LgWidth } from '@components/Responsive';
import useOnboardingProcess from '@hooks/useOnboardingProcess';

import './PanelSection.less';

interface PanelSectionProps {
  name: string;
  count?: number;
  dropdownMenu?: ItemType[] | null;
  extraction?: React.ReactElement | null;
  onDropdownHandler?(key: string): void;
  onClose?(): void;
  isPinned?: boolean;
  onTogglePin?(): void;
  children: ReactNode;
}

function PanelHeader({
  name,
  count,
  extraction,
  dropdownMenu,
  onClose,
  onTogglePin,
  isPinned,
  onDropdownHandler
}: {
  name: string;
  count: number | null;
  dropdownMenu: ItemType[] | null;
  extraction: React.ReactElement | null;
  onClose?(): void;
  isPinned?: boolean;
  onTogglePin?(): void;
  onDropdownHandler: ((key: string) => void) | null;
}) {
  const { tourData, goToStep } = useOnboardingProcess();
  const isAnnouncementMediaViewerTourPin =
    tourData?.type === 'announcement-media-viewer' && goToStep === 2;
  const isAnnouncementMediaViewerTourMenu =
    tourData?.type === 'announcement-media-viewer' && goToStep === 3;
  return (
    <Row
      className="panel-section_head"
      wrap={false}
      justify="space-between"
      align="middle"
    >
      <Col>
        <Row gutter={8} align="middle">
          <Col>{name}</Col>
          {count ? (
            <Col>
              <div className="panel-section_head-label">{count}</div>
            </Col>
          ) : (
            ''
          )}
        </Row>
      </Col>
      <Col className="panel-section_head-actions">
        <Row gutter={10} align="middle" wrap={false}>
          {extraction && <Col>{extraction}</Col>}
          {dropdownMenu && (
            <Col>
              <Dropdown
                menu={{
                  onClick: ({
                    key,
                    domEvent
                  }: {
                    key: string;
                    domEvent: any;
                  }) => {
                    domEvent.stopPropagation();
                    if (onDropdownHandler) {
                      onDropdownHandler(key);
                    }
                  },
                  selectable: false,
                  items: dropdownMenu
                }}
                // open={isAnnouncementMediaViewerTourMenu ? true : undefined}
                prefixCls="media_viewer_menu"
                overlayClassName={classNames(
                  'panel-section_head-overlay',
                  'tour-announcement-media-viewer-panel-section-head-overlay',
                  {
                    'tour-announcement-media-viewer-panel-section-head-overlay-active':
                      isAnnouncementMediaViewerTourMenu
                  }
                )}
                placement="bottomRight"
                trigger={['click']}
              >
                <Button
                  className="media_viewer_btn b-only-icon j-tour-announcement-media-viewer-panel-section-head-btn"
                  onClick={(event) => event.stopPropagation()}
                >
                  <ActionSvg className="b-white-icon" />
                </Button>
              </Dropdown>
            </Col>
          )}
          {!!onTogglePin && (
            <Col>
              <Tooltip
                title={isPinned ? 'Unpin panel' : 'Pin panel'}
                overlayClassName="media_viewer_tooltip"
              >
                <Button
                  className={classNames(
                    'media_viewer_btn b-only-icon panel-section_head-pin',
                    'tour-announcement-media-viewer-panel-section-head-pin',
                    {
                      'panel-section_head-pin--active': isPinned,
                      'tour-announcement-media-viewer-panel-section-head-pin-active':
                        isAnnouncementMediaViewerTourPin
                    }
                  )}
                  onClick={onTogglePin}
                >
                  <PinSecondarySvg className="b-white-icon" />
                </Button>
              </Tooltip>
            </Col>
          )}
          {!!onClose && (
            <Col>
              <Button
                className="media_viewer_btn b-only-icon"
                onClick={onClose}
              >
                <CrossSvg className="b-white-icon" width={20} height={20} />
              </Button>
            </Col>
          )}
        </Row>
      </Col>
    </Row>
  );
}

function PanelSection({
  name,
  count,
  extraction,
  onDropdownHandler,
  onClose,
  isPinned,
  onTogglePin,
  dropdownMenu,
  children
}: PanelSectionProps) {
  return (
    <div className="panel-section">
      <LgWidth>
        <PanelHeader
          name={name}
          count={count || null}
          extraction={extraction || null}
          dropdownMenu={dropdownMenu || null}
          onDropdownHandler={onDropdownHandler || null}
          onClose={onClose}
          isPinned={isPinned}
          onTogglePin={onTogglePin}
        />
      </LgWidth>

      {children}
    </div>
  );
}

export default PanelSection;
